import { FC, useState, useRef, useEffect, useMemo } from "react";
import { useLoader } from "@react-three/fiber";
import {
  BufferGeometry,
  NormalBufferAttributes,
  Mesh,
  Material,
  BackSide,
  ShapeGeometry,
  MeshBasicMaterial,
  Color,
  LineSegments,
  LineBasicMaterial,
  BufferAttribute,
} from "three";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader";
import { SVGLoader } from "three/examples/jsm/loaders/SVGLoader";

import { Triple } from "_/types";

import bedSTL from "./hylo_bed.stl";
import logoSVG from "./aon3d_logo.svg";

// Position offset for the bed
const OFFSET = [-15, -15, 0] as Triple<number>;

export type BedConfig = {
  surfaceSideLength: number;
};

type GridProps = {
  position?: Triple<number>;
  visible: boolean;
};

const DEFAULT_POSITION: Triple<number> = [0, 0, 0];

/**
 * Grid lines for the bed.
 */
export const Grid = ({ position = DEFAULT_POSITION, visible }: GridProps) => {
  const [lines, gridWidth, gridHeight] = useMemo(() => {
    // Dimensions experimentally found, based on the vacuum chuck STL file.
    const gridWidthInner = 664;
    const gridHeightInner = 462;

    const cellCountX = 10;
    const cellCountY = 10;

    const cellWidth = gridWidthInner / cellCountX;
    const cellHeight = gridHeightInner / cellCountY;

    const vertexArray: number[] = [];

    for (let i = 0; i <= cellCountX; i++) {
      const x = i * cellWidth;
      vertexArray.push(x, 0, 0);
      vertexArray.push(x, gridHeightInner, 0);
    }

    for (let i = 0; i <= cellCountY; i++) {
      const y = i * cellHeight;
      vertexArray.push(0, y, 0);
      vertexArray.push(gridWidthInner, y, 0);
    }

    // Two diagonal vacuum chuck channel lines.
    vertexArray.push(cellWidth * 2, cellHeight * 2, 0);
    vertexArray.push(cellWidth * 8, cellHeight * 8, 0);
    vertexArray.push(cellWidth * 2, cellHeight * 8, 0);
    vertexArray.push(cellWidth * 8, cellHeight * 2, 0);

    const positionArray = new Float32Array(vertexArray.length);

    for (let i = 0; i < vertexArray.length; i++) {
      positionArray[i * 3 + 0] = vertexArray[i * 3 + 0];
      positionArray[i * 3 + 1] = vertexArray[i * 3 + 1];
      positionArray[i * 3 + 2] = vertexArray[i * 3 + 2];
    }

    const geometry = new BufferGeometry();
    geometry.setAttribute("position", new BufferAttribute(positionArray, 3));

    const lineMaterial = new LineBasicMaterial({ color: 0xaaaabb });
    const linesInner = new LineSegments(geometry, lineMaterial);
    linesInner.frustumCulled = false;

    return [linesInner, gridWidthInner, gridHeightInner];
  }, []);

  if (!visible) {
    return null;
  }

  return (
    <primitive
      object={lines}
      position={[
        position[0] - gridWidth / 2,
        position[1] - gridHeight / 2,
        position[2],
      ]}
    />
  );
};

type LogoOverlayProps = {
  scale?: number;
  position?: Triple<number>;
};

/**
 * Logo overlay.
 */
const LogoOverlay = ({ scale = 1, position }: LogoOverlayProps) => {
  // Load the SVG file
  const svg = useLoader(SVGLoader, logoSVG);

  // Parse the SVG
  const shapes = useMemo(
    () =>
      svg.paths.flatMap((path, i) => {
        const shapesInner = path.toShapes(true);
        return shapesInner.map((shape, j) => {
          const geometry = new ShapeGeometry(shape);
          const material = new MeshBasicMaterial({
            color: new Color(0xadadad),
            side: BackSide,
          });

          return (
            <mesh geometry={geometry} material={material} key={`${i}-${j}`} />
          );
        });
      }),
    [svg]
  );

  return (
    <group
      scale={[scale, scale, scale]}
      position={position}
      rotation={[Math.PI, 0, 0]}
    >
      {shapes}
    </group>
  );
};

type BedProps = {
  showBed?: boolean;
  showGrid?: boolean;
};

export const Bed: FC<BedProps> = ({ showBed = true, showGrid = true }) => {
  const loadedGeometry = useLoader(STLLoader, bedSTL);

  const [dimensions, setDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);

  const geometry = Array.isArray(loadedGeometry)
    ? loadedGeometry[0]
    : loadedGeometry;

  const meshRef = useRef<Mesh<
    BufferGeometry<NormalBufferAttributes>,
    Material | Material[]
  > | null>(null);

  useEffect(() => {
    if (geometry && meshRef.current) {
      // Compute the translation vector to move the bottom left corner to the origin
      geometry.computeBoundingBox();
      const bbox = geometry.boundingBox;

      if (bbox === null) {
        // Unreachable.
        throw new Error("BBOX could not be computed!");
      }

      const offset = bbox.min;

      // Translate the geometry
      geometry.translate(-offset.x, -offset.y, 0);

      // Calculate the dimensions
      setDimensions({
        width: bbox.max.x - bbox.min.x,
        height: bbox.max.y - bbox.min.y,
      });
    }
  }, [geometry]);

  const gridPosition: Triple<number> = dimensions
    ? [dimensions.width / 2, dimensions.height / 2, 0]
    : [0, 0, 0];

  // Logo position coordinates "eyeballed" from the initial Hylo bed overlay .png
  const logoPosition: Triple<number> = [40, 40, 0.01];

  return (
    <group position={OFFSET}>
      <mesh geometry={geometry} ref={meshRef} visible={showBed}>
        <meshStandardMaterial attach="material" color={0x444444} />
      </mesh>
      <LogoOverlay scale={0.2} position={logoPosition} />
      <Grid position={gridPosition} visible={showGrid} />
    </group>
  );
};
